import { type DataEditorProps } from "@glideapps/glide-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type AdaptedSpreadsheetSheet,
  type SpreadsheetSearchMatch,
} from "./adapter";
import { type SheetSearchControlProps } from "./sheet-search-control";
import { volcaColor, volcaColorAlpha } from "./spreadsheet-theme";

/**
 * Search highlights: ONE hue, two states.
 *
 * Every match takes the same blue fill and the active one adds a blue outline.
 * Distinguishing the active match is universal (Chrome, VS Code, Sheets all do
 * it) but the convention is one colour at two strengths — a second hue would
 * say "different kind of thing" when the truth is "same thing, you are here".
 *
 * Blue specifically: a match is a highlighted cell, which is what blue means
 * Navigating to a match also moves the grid selection onto it, so the active
 * match is genuinely the selected cell — the outline reinforces the selection
 * ring rather than competing with it.
 *
 * Others sit ABOVE the selection's own 10% fill so a match still reads as a
 * match on a sheet where something else is selected; the active one is the
 * darker step, so "you are here" is never the weaker mark.
 *
 * Built on call, not at module load: the tokens resolve off `:root`.
 */
const otherSearchMatchColor = () => volcaColorAlpha("--blue-9", 0.22);
/**
 *
 * Going to a match moves the grid selection onto it, so nine times in ten the
 * outline you actually see on the active match is Glide's selection ring painted
 * over this one. Setting anything else here (blue-80 was tried, and measured
 * back as `rgb(0,119,217)` — the ring, not the token) only changes the tenth
 * case, where the user has clicked away and the ring is elsewhere. Matching the
 * ring means the active match looks the same either way.
 */
const activeSearchMatchColor = () => volcaColor("--brand-bold");

interface UseSheetSearchParams {
  activeSheetId: string | null;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
}

export const useSheetSearch = ({
  activeSheetId,
  adaptedSheet,
}: UseSheetSearchParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusRequestVersion, setFocusRequestVersion] = useState(0);
  const [query, setQuery] = useState("");
  const [rawActiveMatchIndex, setRawActiveMatchIndex] = useState(0);
  // Search matches are re-derived after every edit. Keep the scroll target
  // separate so only an explicit search action can recenter the grid.
  const [navigationTarget, setNavigationTarget] =
    useState<SpreadsheetSearchMatch | null>(null);

  const matches = useMemo(
    () => adaptedSheet?.getSearchMatches(query) ?? [],
    [adaptedSheet, query],
  );
  const activeMatchIndex =
    matches.length === 0
      ? 0
      : Math.min(rawActiveMatchIndex, matches.length - 1);
  const activeMatch: SpreadsheetSearchMatch | null =
    matches[activeMatchIndex] ?? null;
  const highlightRegions = useMemo<DataEditorProps["highlightRegions"]>(() => {
    if (matches.length === 0) return undefined;

    return matches.flatMap((match, index) => {
      const range = { x: match.col, y: match.row, width: 1, height: 1 };
      // "solid", not "no-outline": Glide's "no-outline" drops the fill as well
      // as the stroke, which erases the match entirely. So every match carries a
      // depth of its outline instead — see `activeSearchMatchColor`.
      const fill = {
        color: otherSearchMatchColor(),
        range,
        style: "solid" as const,
      };
      if (index !== activeMatchIndex) return [fill];

      // The active match keeps the fill every match gets and ADDS an outline on
      // top of it, rather than swapping one for the other. Glide's
      // "solid-outline" barely fills, so styling the active match with it alone
      // left the one cell you are standing on as the palest of the set — the
      // "you are here" mark has to be the stronger one, not a different one.
      return [
        fill,
        {
          color: activeSearchMatchColor(),
          range,
          style: "solid-outline" as const,
        },
      ];
    });
  }, [activeMatchIndex, matches]);

  useEffect(() => {
    setRawActiveMatchIndex(0);
    setNavigationTarget(null);
  }, [activeSheetId]);

  const changeQuery = useCallback(
    (nextQuery: string) => {
      const firstMatch = adaptedSheet?.getSearchMatches(nextQuery)[0] ?? null;

      setQuery(nextQuery);
      setRawActiveMatchIndex(0);
      setNavigationTarget(firstMatch ? { ...firstMatch } : null);
    },
    [adaptedSheet],
  );

  const clear = useCallback(() => {
    setQuery("");
    setRawActiveMatchIndex(0);
    setNavigationTarget(null);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    clear();
  }, [clear]);

  const goToPrevious = useCallback(() => {
    if (matches.length === 0) return;

    const previousIndex =
      (activeMatchIndex - 1 + matches.length) % matches.length;
    setRawActiveMatchIndex(previousIndex);
    setNavigationTarget({ ...matches[previousIndex] });
  }, [activeMatchIndex, matches]);

  const goToNext = useCallback(() => {
    if (matches.length === 0) return;

    const nextIndex = (activeMatchIndex + 1) % matches.length;
    setRawActiveMatchIndex(nextIndex);
    setNavigationTarget({ ...matches[nextIndex] });
  }, [activeMatchIndex, matches]);

  const open = useCallback(() => {
    setIsOpen(true);
    setFocusRequestVersion((previous) => previous + 1);
  }, []);

  const controlProps = useMemo<SheetSearchControlProps>(
    () => ({
      isOpen,
      focusRequestVersion,
      query,
      matchCount: matches.length,
      activeMatchIndex,
      onOpen: open,
      onClose: close,
      onQueryChange: changeQuery,
      onPreviousMatch: goToPrevious,
      onNextMatch: goToNext,
    }),
    [
      activeMatchIndex,
      changeQuery,
      close,
      focusRequestVersion,
      goToNext,
      goToPrevious,
      isOpen,
      matches.length,
      open,
      query,
    ],
  );

  return {
    activeMatch,
    controlProps,
    highlightRegions,
    navigationTarget,
    open,
  };
};
